import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, CheckCircle2, XCircle, CalendarDays } from "lucide-react";
import { Reveal } from "@/components/landing/Reveal";
import { CoverageMap } from "@/components/landing/CoverageMap";
import { SERVICE_AREAS, CITY_LINKS, checkZip } from "@/lib/site";

export const Coverage = () => {
  const [zip, setZip] = useState("");
  const [result, setResult] = useState(null);

  const handleCheck = (e) => {
    e.preventDefault();
    const city = checkZip(zip.trim());
    setResult(city ? { ok: true, city } : { ok: false });
  };

  return (
    <section id="coverage" data-testid="coverage-section" className="bg-slate-50 py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-blue sm:text-sm">Service areas</p>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-brand-navy sm:text-3xl lg:text-4xl">
              Free pickup &amp; delivery across the East Valley
            </h2>
            <p className="mt-4 max-w-lg text-base leading-relaxed text-slate-600">
              Our routes run Monday through Saturday. If you&apos;re in one of these cities, pickup and
              delivery are always free — no minimums, no fees.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              {SERVICE_AREAS.map((city, i) => (
                <motion.span
                  key={city}
                  initial={{ opacity: 0, scale: 0.85 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06, duration: 0.4 }}
                  className="inline-block"
                >
                  <Link
                    to={CITY_LINKS[city]}
                    data-testid={`coverage-city-${city.toLowerCase().replace(/\s/g, "-")}`}
                    className="flex items-center gap-2 rounded-full border border-brand-blue/25 bg-white px-4 py-2 text-sm font-bold text-brand-navy shadow-sm transition-colors hover:border-brand-blue hover:bg-brand-blue-light"
                  >
                    <MapPin className="h-4 w-4 text-fall-orange" />
                    {city}
                  </Link>
                </motion.span>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <CoverageMap />
          </Reveal>

          <Reveal delay={0.25}>
            <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-xl shadow-brand-blue/5">
              <div className="flex items-center gap-3">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-fall-amber/15 text-fall-orange">
                  <CalendarDays className="h-6 w-6" />
                </span>
                <div>
                  <h3 className="font-display text-lg font-bold text-brand-navy">Are you on a route?</h3>
                  <p className="text-sm text-slate-500">Instant zip check — no signup needed.</p>
                </div>
              </div>

              <form onSubmit={handleCheck} className="mt-6 flex gap-2">
                <input
                  data-testid="coverage-zip-input"
                  value={zip}
                  onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                  placeholder="Your zip code"
                  inputMode="numeric"
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold focus:border-brand-blue focus:outline-none"
                />
                <button
                  data-testid="coverage-check-button"
                  type="submit"
                  className="shrink-0 rounded-xl bg-brand-navy px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-brand-navy-light"
                >
                  Check
                </button>
              </form>

              {result && (
                <motion.p
                  data-testid="coverage-result"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`mt-4 flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold ${
                    result.ok ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-600"
                  }`}
                >
                  {result.ok ? (
                    <>
                      <CheckCircle2 className="h-4 w-4" /> Yes! Free pickup &amp; delivery in {result.city}.
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4" /> That zip isn&apos;t on a route yet — call 480-940-6023 and we&apos;ll try to help.
                    </>
                  )}
                </motion.p>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
